import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, CreditCardIcon, SparklesIcon } from "lucide-react";
import { toast } from "sonner";
import { useLearner } from "../../contexts/LearnerContext";
import { billingHistory, pricingPlans } from "../../data/plans";
import { ProfileHeader } from "../../components/profile/ProfileHeader";
import { Card } from "../../components/ui/Card";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { Segmented } from "../../components/ui/Segmented";
import { Modal } from "../../components/ui/Modal";
import { Tag } from "../../components/ui/Tag";
import { spring } from "../../utils/motion";
export function Subscription() {
  const {
    plan,
    setPlan
  } = useLearner();
  const [cycle, setCycle] = useState<"monthly" | "yearly">("yearly");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [loading, setLoading] = useState(false);
  const plus = pricingPlans.find(p => p.id === "plus")!;
  const price = cycle === "monthly" ? plus.monthly : plus.yearly;
  const upgrade = () => {
    setLoading(true);
    window.setTimeout(() => {
      setLoading(false);
      setPlan("plus");
      toast.success("Welcome to Plus", {
        description: "Your 7-day free trial has started."
      });
    }, 900);
  };
  return <div className="mx-auto max-w-5xl px-4 py-6 md:px-8 md:py-10" data-id="element-1559" data-mp="pgmcqp4krpo1">
      <ProfileHeader data-id="element-1560" data-mp="13gopxy1xtuwy" />

      {plan === "plus" ? <Card as="section" labelledBy="plan-title" className="mt-8 p-6 md:p-8" data-id="element-1561" data-mp="13fdow5zvn19j">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between" data-id="element-1562" data-mp="yhc51iummxx0">
            <div data-id="element-1563" data-mp="1d5os1qbql6fd">
              <Tag tone="brand" icon={SparklesIcon} data-id="element-1564" data-mp="1d689l5xh5ssv">
                Active
              </Tag>
              <h2 id="plan-title" className="mt-3 text-3xl font-bold tracking-[-0.02em]" data-id="element-1565" data-mp="188owwagfdo31">
                PolyLexa Plus
              </h2>
              <p className="mt-1 text-[15px] text-ink-2" data-id="element-1566" data-mp="1i6csf2luuzm9">€9.99 / month · renews on October 1, 2026</p>
              <ul className="mt-5 grid gap-2 sm:grid-cols-2" data-id="element-1567" data-mp="1p9ja1p56xbtk">
                {plus.features.map(f => <li key={f} className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1568" data-mp="1u7ujvzvhjmbs">
                    <CheckIcon className="h-4 w-4 text-good" strokeWidth={3} data-id="element-1569" data-mp="1z7qq85rnnpui" /> {f}
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col gap-3 md:w-56" data-id="element-1570" data-mp="23zi5hk5d295c">
              <BrutalButton variant="surface" full onClick={() => toast("Billing portal would open here")} data-id="element-1571" data-mp="280k8mc2ef5cl">
                <CreditCardIcon className="h-4 w-4" data-id="element-1572" data-mp="2cxu6mg3uspc1" /> Update payment
              </BrutalButton>
              <button type="button" onClick={() => setConfirmCancel(true)} className="focus-ring rounded-lg py-2 text-sm font-semibold text-ink-2 underline underline-offset-4 hover:text-bad" data-id="element-1573" data-mp="2hz004g5c8gmm">
                Cancel subscription
              </button>
            </div>
          </div>
        </Card> : <section aria-labelledby="upgrade-title" className="tex-dots mt-8 rounded-panel border-3 border-line bg-brand p-6 text-white shadow-brutal-lg md:p-8" data-id="element-1574" data-mp="35t1qrwmgr6nd">
          <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-end" data-id="element-1575" data-mp="311aaqg0xojbh">
            <div data-id="element-1576" data-mp="2x1rnvepjxy60">
              <p className="text-sm font-semibold text-white/85" data-id="element-1577" data-mp="2x186bz3tdbsi">You're on the free plan</p>
              <h2 id="upgrade-title" className="mt-1 max-w-[20ch] text-3xl font-bold leading-tight tracking-[-0.02em] md:text-4xl" data-id="element-1578" data-mp="2s3ggrbqn61rh">
                Learn every pair you want, without running out of hearts.
              </h2>
              <ul className="mt-5 grid gap-2 sm:grid-cols-2" data-id="element-1579" data-mp="3koq1m4maes08">
                {plus.features.map(f => <li key={f} className="flex items-center gap-2 text-[15px]" data-id="element-1580" data-mp="3asda9de7sq8n">
                    <CheckIcon className="h-4 w-4 text-accent" strokeWidth={3} data-id="element-1581" data-mp="3auhc7psjzf6a" /> {f}
                  </li>)}
              </ul>
            </div>
            <div className="rounded-card border-3 border-line bg-surface p-5 text-ink md:w-72" data-id="element-1582" data-mp="1zc6gwwkl3wm7">
              <Segmented id="sub-cycle" label="Billing cycle" value={cycle} onChange={setCycle} full options={[{
            value: "monthly",
            label: "Monthly"
          }, {
            value: "yearly",
            label: "Yearly",
            badge: "-40%"
          }]} data-id="element-1583" data-mp="3n8uuhe8tsail" />
              <div className="mt-4 flex items-baseline gap-1 overflow-hidden" data-id="element-1584" data-mp="assbcqptnw3z">
                <AnimatePresence mode="popLayout" initial={false} data-id="element-1585" data-mp="aqo9eebhh76c">
                  <motion.span key={price} initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} exit={{
                y: -20,
                opacity: 0
              }} transition={spring.bouncy} className="text-4xl font-bold tracking-[-0.02em]" data-id="element-1586" data-mp="etkldm6iq51b">
                    €{price}
                  </motion.span>
                </AnimatePresence>
                <span className="text-sm text-ink-2" data-id="element-1587" data-mp="2izvr8hzrjy1c">/ month</span>
              </div>
              <p className="mt-1 text-xs text-ink-2" data-id="element-1588" data-mp="2j1ypfpb2kxh5">{cycle === "yearly" ? "€71.88 billed yearly" : "Billed monthly, cancel any time"}</p>
              <div className="mt-4" data-id="element-1589" data-mp="2s128wue5few6">
                <BrutalButton full loading={loading} onClick={upgrade} data-id="element-1590" data-mp="2s00y8r11ouez">
                  Start 7-day free trial
                </BrutalButton>
              </div>
            </div>
          </div>
        </section>}

      <section aria-labelledby="billing-title" className="mt-10" data-id="element-1591" data-mp="1z9shn3m5qi2i">
        <h2 id="billing-title" className="text-xl font-bold tracking-[-0.01em]" data-id="element-1592" data-mp="1vc4h9l1p3x38">
          Billing history
        </h2>
        <div className="mt-4 overflow-x-auto rounded-card border-3 border-line bg-surface" data-id="element-1593" data-mp="29415he08tmuc">
          <table className="w-full min-w-[520px] text-left text-[15px]" data-id="element-1594" data-mp="24950ld9wr4zt">
            <thead className="border-b-3 border-line bg-sunken text-xs font-bold text-ink-2" data-id="element-1595" data-mp="1gkcahxfyvrd4">
              <tr data-id="element-1596" data-mp="1blsojx1difkr">
                <th scope="col" className="px-4 py-2.5" data-id="element-1597" data-mp="1bmaign0kchpi">Date</th>
                <th scope="col" className="px-4 py-2.5" data-id="element-1598" data-mp="3bpldn7y8an3w">Description</th>
                <th scope="col" className="px-4 py-2.5" data-id="element-1599" data-mp="3gnmynyf7swi0">Amount</th>
                <th scope="col" className="px-4 py-2.5" data-id="element-1600" data-mp="3gltqufsv868f">Status</th>
              </tr>
            </thead>
            <tbody data-id="element-1601" data-mp="3lgpukd7gsi1d">
              {billingHistory.map(b => <tr key={b.id} className="border-b-2 border-line/10 last:border-b-0" data-id="element-1602" data-mp="3qcen3wl2gyo6">
                  <td className="px-4 py-3 text-ink-2" data-id="element-1603" data-mp="2sqzfa0u3ja3o">{b.date}</td>
                  <td className="px-4 py-3 text-ink" data-id="element-1604" data-mp="2xp0u29khon9c">{b.description}</td>
                  <td className="px-4 py-3 font-semibold text-ink" data-id="element-1605" data-mp="2xr4zivy1dubq">{b.amount}</td>
                  <td className="px-4 py-3" data-id="element-1606" data-mp="32mkczms4to7m">
                    <Tag tone={b.status === "Paid" ? "good" : "neutral"} data-id="element-1607" data-mp="36pgzvpopg6hd">{b.status}</Tag>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      <Modal open={confirmCancel} onClose={() => setConfirmCancel(false)} title="Cancel Plus?" footer={<>
            <BrutalButton variant="surface" onClick={() => {
        setPlan("free");
        setConfirmCancel(false);
        toast("Plus cancelled", {
          description: "You keep Plus until October 1, 2026."
        });
      }} data-id="element-1609" data-mp="26jmxysnzlpn6">
              Cancel Plus
            </BrutalButton>
            <BrutalButton onClick={() => setConfirmCancel(false)} data-id="element-1610" data-mp="1nrkfdbrsbzaa">Keep Plus</BrutalButton>
          </>} data-id="element-1608" data-mp="lyykitfmyrez">
        You'll keep Plus until the end of your billing period. After that you'll go back to 1 course pair and 5 hearts per lesson — your saved words stay safe.
      </Modal>
    </div>;
}